import React, { useState, useEffect } from 'react';
import clipboard from 'clipboard-js';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { environmentSelectors } from '@lifesize/nucleus';
import { ReactComponent as CalendarIcon } from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import { ReactComponent as ClipboardIcon } from '@lifesize/ux-assets/vectors/svgIcons/clipboard.svg';
import { ReactComponent as EmailIcon } from '@lifesize/ux-assets/vectors/svgIcons/email.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import Button from 'components/Button/Button';
import { getDomain } from 'utils/UrlUtil';
import lsConfig from 'utils/global-config';
import logger from 'utils/logger';
import classes from './ScheduleInfo.module.scss';
import { generateOutlookInvite } from 'api/azure-api';
import { generateGCalInvite } from 'api/google-api';
import { generateICalInvite } from 'utils/calendar-utils';
import { calendarApiError } from 'actions/Calendar';
import { calendars } from 'constants/Calendar';

const messages = defineMessages({
  copy: {
    id: 'components.scheduleModal.clipboard.copy',
    defaultMessage: 'Copy'
  },
  copied: {
    id: 'components.scheduleModal.clipboard.copied',
    defaultMessage: 'Copied!'
  },
  copiedNotification: {
    id: 'components.scheduleModal.clipboard.copiedNotification',
    defaultMessage: 'The invitation has been copied to your clipboard.'
  },
  inviteTitle: {
    id: 'components.scheduleModal.invite.title',
    defaultMessage: 'lifesize_invite.ics'
  }
});

let invitationPromise;
let mailtoPromise;

const handleCalendarClick = async (props, dispatch) => {
  const {
    myInfo,
    isOutlookAuthenticated,
    isGoogleAuthenticated,
    isGoogleAvailable,
    isOutlookAvailable,
    contact,
    outlookToken,
    isDesktop,
    intl
  } = props;

  try {
    const invitation = await invitationPromise;
    const today = new Date();
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours() + 2, 0, 0);

    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours() + 1, 0, 0);

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const start = {
      dateTime: startDate,
      timeZone
    };

    const end = {
      dateTime: endDate,
      timeZone
    };

    const id = `${myInfo.id}${today.toISOString()}@${getDomain()}`;

    const createICalFromContact = async () => {
      const inviteTitle = intl.formatMessage(messages.inviteTitle);
      await generateICalInvite(invitation, id, today, contact.inviteURL, startDate, endDate, inviteTitle);
    };

    try {
      if (isOutlookAvailable && !isDesktop && !isGoogleAuthenticated) {
        await generateOutlookInvite(invitation, contact.inviteURL, outlookToken, startDate, endDate, id);
      } else if (isGoogleAvailable && !isDesktop && !isOutlookAuthenticated) {
        await generateGCalInvite(invitation, start, end);
      } else {
        await createICalFromContact();
      }
    } catch {
      await createICalFromContact();
    }
  } catch (error) {
    dispatch(calendarApiError(error, calendars.google));
  }
};

interface Props {
  myInfo: { id: string };
  contact: { inviteURL: string; id: string; name: string };
  intl: { formatMessage: Function };
  notifyWithTimeout: () => void;
  getInviteForExtension: () => void;
  getInviteMailLinkForExtension: () => void;
  outlookToken: string;
  isOutlookAuthenticated: boolean;
  isGoogleAuthenticated: boolean;
  isGoogleAvailable: boolean;
}

const copyInviteText = async (invitationPromise, notifyWithTimeout, message) => {
  try {
    const invitation = await invitationPromise;
    clipboard.copy({ 'text/plain': invitation.body });
    // trigger on-screen notification
    notifyWithTimeout(message);
  } catch (e) {
    logger.error(`Unable to copy invitation: ${e}`);
  }
};

const copyLinkText = (inviteURL: string, setCopied: Function) => {
  clipboard.copy({ 'text/plain': inviteURL });
  setCopied(true);
};

const handleEmailClick = async (mailtoPromise) => {
  try {
    const mailto = await mailtoPromise;
    // Create and click email link
    const element = document.createElement('a');
    element.setAttribute('href', mailto);

    element.style.display = 'none';
    document.body.appendChild(element);

    lsConfig.skipTimeOnBeforeUnload(1000);
    element.click();

    document.body.removeChild(element);
    // blur the button so that it is no longer the activeElement (so onBeforeUnload is again active)
    document.getElementById('btnSendScheduleEmail')?.blur();
  } catch (err) {
    logger.error(`Unable to create mailto link: ${err}`);
  }
};

const copyLinkSection = (intl, isCopied, setCopied, inviteURL, notifyWithTimeout) => {
  const copyStatus = isCopied ? intl.formatMessage(messages.copied) : intl.formatMessage(messages.copy);

  const handleCopyClick = () => {
    copyLinkText(inviteURL, setCopied);
  };

  return (
    <div className={classes.callLinkContainer}>
      <div className={classes.label}>
        <FormattedMessage id="components.scheduleModal.meetingLinkLabel" defaultMessage="Meeting Link" />
      </div>
      <div className={classes.link}>
        {inviteURL}
        <button
          onClick={handleCopyClick}
          className={`${classes.copyStatus} ${isCopied ? classes.copied : ''} secretButton`}
          data-testid="copy-meeting-link"
        >
          {copyStatus}
        </button>
      </div>
    </div>
  );
};

const ScheduleInfo = (props: Props) => {
  const { intl, myInfo, contact, notifyWithTimeout, getInviteForExtension, getInviteMailLinkForExtension } = props;

  const [isCopied, setCopied] = useState(false);
  const dispatch = useDispatch();
  const isDesktop = useSelector((state) => environmentSelectors.platform(state).isDesktop);

  useEffect(() => {
    invitationPromise = getInviteForExtension();
    mailtoPromise = getInviteMailLinkForExtension();
  }, [getInviteForExtension, getInviteMailLinkForExtension]);

  return (
    <section className={classes.scheduleInfoContainer}>
      <div className={classes.scheduleInfoHeader}>
        {myInfo && contact.id === myInfo.id ? (
          <FormattedMessage id="schedule.info.selfTitle" defaultMessage="Share my invitation" />
        ) : (
          <FormattedMessage
            id="schedule.info.title"
            defaultMessage="Share {name}'s invitation"
            values={{ name: contact.name }}
          />
        )}
      </div>
      <div className={classes.buttonBar}>
        <Button
          dataAttrs={{ 'data-onbeforeunload': 'ignore', 'data-testid': 'schedule-email' }}
          id="btnSendScheduleEmail"
          onClick={() => handleEmailClick(mailtoPromise)}
          style={{ minWidth: '150px', padding: 10, margin: '0 5px' }}
        >
          <EmailIcon />
          <FormattedMessage id="components.scheduleModal.emailLabel" defaultMessage="Email" />
          <Tooltip
            label={<FormattedMessage id="components.scheduleModal.emailTooltip" defaultMessage="Share by email" />}
            style={{ marginTop: 7, minWidth: 'auto', top: '100%' }}
          />
        </Button>
        <Button
          dataAttrs={{ 'data-testid': 'schedule-calendar' }}
          onClick={() => {
            handleCalendarClick({ ...props, isDesktop }, dispatch);
          }}
          style={{ minWidth: 'auto', padding: 10, margin: '0 5px' }}
        >
          <CalendarIcon id="components.scheduleModal.calendar" />
          <FormattedMessage id="components.scheduleModal.calendarLabel" defaultMessage="Calendar" />
          <Tooltip
            label={<FormattedMessage id="components.scheduleModal.calendarTooltip" defaultMessage="Create event" />}
            style={{ marginTop: 7, minWidth: 'auto', top: '100%' }}
          />
        </Button>
        <Button
          dataAttrs={{ 'data-testid': 'schedule-clipboard' }}
          onClick={() => {
            copyInviteText(invitationPromise, notifyWithTimeout, intl.formatMessage(messages.copiedNotification));
          }}
          style={{ minWidth: 'auto', padding: 10, margin: '0 5px' }}
        >
          <ClipboardIcon data-testid="clipboard-icon" />
          <FormattedMessage id="components.scheduleModal.clipboard" defaultMessage="Clipboard" />
          <Tooltip
            label={
              <FormattedMessage id="components.scheduleModal.tooltip.clipboard" defaultMessage="Copy to clipboard" />
            }
            style={{ marginTop: 7, minWidth: 'auto', top: '100%', margin: '0 5px' }}
          />
        </Button>
      </div>
      {contact.inviteURL && copyLinkSection(intl, isCopied, setCopied, contact.inviteURL, notifyWithTimeout)}
    </section>
  );
};

export default injectIntl(ScheduleInfo);
